import { InlineHelp } from '@/features/help';
import {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
} from 'react';
import {
  NavigationType,
  UNSAFE_LocationContext,
  UNSAFE_RouteContext,
  useLocation,
  useNavigate,
  useOutlet,
  type Location,
} from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogAction,
  AlertDialogCancel,
} from '@/components/ui/alert-dialog';
import { useAppearance } from '@/features/appearance';
import { WorkTabContext } from './work-tab-context';
import { workPath, type WorkPath } from './work-path';

interface Entry {
  id: string;
  path: WorkPath;
  location: Location;
  content: ReactElement | null;
  routeContext: React.ContextType<typeof UNSAFE_RouteContext>;
  title?: string;
  dirty: boolean;
  busy: boolean;
}

function Panel({
  entry,
  active,
  companion,
  update,
  close,
}: {
  entry: Entry;
  active: boolean;
  companion: boolean;
  update: (
    id: string,
    changes: { title?: string; dirty?: boolean; busy?: boolean; path?: string; listPath?: string },
  ) => void;
  close: (id: string) => void;
}): JSX.Element {
  const scrollPosition = useRef(0);
  useLayoutEffect(() => {
    if (!active) return;
    if (scrollPosition.current > 0)
      window.scrollTo({ top: scrollPosition.current, behavior: 'instant' });
    const remember = (): void => {
      scrollPosition.current = window.scrollY;
    };
    window.addEventListener('scroll', remember, { passive: true });
    return () => window.removeEventListener('scroll', remember);
  }, [active]);
  const boundUpdate = useCallback(
    (changes: {
      title?: string;
      dirty?: boolean;
      busy?: boolean;
      path?: string;
      listPath?: string;
    }) => update(entry.id, changes),
    [entry.id, update],
  );
  const value = useMemo(
    () => ({
      managed: true,
      companion,
      fieldPrefix: `${entry.id}-`,
      active,
      update: boundUpdate,
      close: () => close(entry.id),
    }),
    [active, companion, boundUpdate, close, entry.id],
  );
  return (
    <section
      id={`work-panel-${entry.id}`}
      role="tabpanel"
      aria-labelledby={`work-tab-${entry.id}`}
      hidden={!active && !companion}
      inert={!active && !companion}
      className={companion ? 'work-companion' : 'work-panel'}
    >
      <UNSAFE_LocationContext.Provider
        value={{ location: entry.location, navigationType: NavigationType.Pop }}
      >
        <UNSAFE_RouteContext.Provider value={entry.routeContext}>
          <WorkTabContext.Provider value={value}>{entry.content}</WorkTabContext.Provider>
        </UNSAFE_RouteContext.Provider>
      </UNSAFE_LocationContext.Provider>
    </section>
  );
}

const availablePath = (): boolean => true;

/** Keep only the central ERP list/editor routes mounted; state never leaves memory. */
export function WorkArea({
  renderCompanion,
  isPathAvailable = availablePath,
}: {
  renderCompanion?: (path: WorkPath) => ReactElement | null;
  isPathAvailable?: (pathname: string) => boolean;
}): JSX.Element {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const outlet = useOutlet();
  const routeContext = useContext(UNSAFE_RouteContext);
  const { variant } = useAppearance();
  const [entries, setEntries] = useState<Entry[]>([]);
  const [seenLocation, setSeenLocation] = useState('');
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const path = workPath(location.pathname, location.search, location.key);

  // Synchronize before children render so a newly visited record mounts exactly once.
  if (seenLocation !== `${location.key}:${location.pathname}${location.search}${location.hash}`) {
    setSeenLocation(`${location.key}:${location.pathname}${location.search}${location.hash}`);
    if (path !== null) {
      setEntries((current) => {
        const existing = current.find((entry) => entry.path.key === path.key);
        if (existing)
          return current.map((entry) =>
            entry.id === existing.id
              ? { ...entry, location, content: outlet, routeContext }
              : entry,
          );
        const storedLocation =
          path.kind === 'record' &&
          /\/new$/.test(location.pathname) &&
          !new URLSearchParams(location.search).has('draft')
            ? {
                ...location,
                search: (() => {
                  const params = new URLSearchParams(location.search);
                  params.set('draft', location.key);
                  return `?${params}`;
                })(),
              }
            : location;
        return [
          ...current,
          {
            id: crypto.randomUUID(),
            path,
            location: storedLocation,
            content: outlet,
            routeContext,
            dirty: false,
            busy: false,
          },
        ];
      });
    }
  }
  const allowedEntries = entries.filter((entry) => isPathAvailable(entry.location.pathname));
  const active = allowedEntries.find((entry) => entry.path.key === path?.key);
  const update = useCallback(
    (
      id: string,
      changes: {
        title?: string;
        dirty?: boolean;
        busy?: boolean;
        path?: string;
        listPath?: string;
      },
    ) => {
      setEntries((current) =>
        current.map((entry) => {
          if (entry.id !== id) return entry;
          const nextPath = changes.path === undefined ? null : workPath(changes.path);
          const list =
            changes.listPath === undefined ? null : workPath(changes.listPath.split('?')[0] ?? '');
          const recordPath =
            nextPath === null ? entry.path : { ...nextPath, listPath: entry.path.listPath };
          const scopedPath =
            list?.kind === 'list' && list.family === entry.path.family
              ? { ...recordPath, listPath: list.listPath }
              : recordPath;
          const next = {
            ...entry,
            ...(changes.title === undefined ? {} : { title: changes.title }),
            ...(changes.dirty === undefined ? {} : { dirty: changes.dirty }),
            ...(changes.busy === undefined ? {} : { busy: changes.busy }),
            path: scopedPath,
          };
          return entry.title === next.title &&
            entry.dirty === next.dirty &&
            entry.busy === next.busy &&
            entry.path.key === next.path.key &&
            entry.path.listPath === next.path.listPath
            ? entry
            : next;
        }),
      );
    },
    [],
  );
  const remove = useCallback(
    (id: string) => {
      const entry = entries.find((item) => item.id === id);
      if (!entry || entry.busy) return;
      setEntries((current) => current.filter((item) => item.id !== id));
      setConfirmId(null);
      if (active?.id === id) {
        const remaining = entries.filter((item) => item.id !== id);
        const target =
          remaining.find(
            (item) => item.path.kind === 'list' && item.path.family === entry.path.family,
          ) ?? remaining.at(-1);
        navigate(
          target
            ? `${target.location.pathname}${target.location.search}${target.location.hash}`
            : entry.path.listPath,
        );
      }
    },
    [active?.id, entries, navigate],
  );
  const close = useCallback(
    (id: string) => {
      const entry = entries.find((item) => item.id === id);
      if (!entry || entry.busy) return;
      if (entry.dirty) setConfirmId(id);
      else remove(id);
    },
    [entries, remove],
  );
  useEffect(() => {
    const warn = (event: BeforeUnloadEvent): void => {
      if (!entries.some((entry) => entry.dirty || entry.busy)) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [entries]);
  const companion =
    variant.composition === 'split' && active?.path.kind === 'record'
      ? allowedEntries.find(
          (entry) => entry.path.kind === 'list' && entry.path.key === active.path.listPath,
        )
      : undefined;
  useEffect(() => {
    if (
      variant.composition !== 'split' ||
      active?.path.kind !== 'record' ||
      companion ||
      !renderCompanion
    )
      return;
    const listPath = workPath(active.path.listPath);
    if (!listPath || !isPathAvailable(listPath.listPath)) return;
    const content = renderCompanion(listPath);
    if (!content) return;
    setEntries((current) =>
      current.some((entry) => entry.path.key === listPath.key)
        ? current
        : [
            ...current,
            {
              id: crypto.randomUUID(),
              path: listPath,
              location: {
                pathname: listPath.listPath,
                search: '',
                hash: '',
                key: 'companion',
                state: null,
              },
              content,
              routeContext,
              dirty: false,
              busy: false,
            },
          ],
    );
  }, [active, companion, isPathAvailable, renderCompanion, routeContext, variant.composition]);
  const pending = entries.find((entry) => entry.id === confirmId);
  return (
    <>
      {allowedEntries.length > 0 && (
        <div
          className="work-tabs mb-4 flex min-w-0 gap-1 overflow-x-auto border-b border-border"
          role="tablist"
          aria-label={t('workTabs.label')}
        >
          {allowedEntries.map((entry, index) => (
            <div
              key={entry.id}
              className="work-tab flex shrink-0 items-center rounded-t-md border border-b-0 border-border"
              data-active={entry.id === active?.id}
            >
              <button
                id={`work-tab-${entry.id}`}
                type="button"
                role="tab"
                aria-selected={entry.id === active?.id}
                aria-controls={`work-panel-${entry.id}`}
                tabIndex={entry.id === active?.id || (!active && index === 0) ? 0 : -1}
                className="min-h-11 max-w-64 truncate px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
                onClick={() =>
                  navigate(
                    `${entry.location.pathname}${entry.location.search}${entry.location.hash}`,
                  )
                }
                onKeyDown={(event) => {
                  let next: Entry | undefined;
                  if (event.key === 'ArrowRight')
                    next = allowedEntries[(index + 1) % allowedEntries.length];
                  if (event.key === 'ArrowLeft')
                    next =
                      allowedEntries[(index - 1 + allowedEntries.length) % allowedEntries.length];
                  if (event.key === 'Home') next = allowedEntries[0];
                  if (event.key === 'End') next = allowedEntries.at(-1);
                  if (event.key === 'Delete') {
                    event.preventDefault();
                    close(entry.id);
                  }
                  if (next) {
                    event.preventDefault();
                    navigate(
                      `${next.location.pathname}${next.location.search}${next.location.hash}`,
                    );
                    document.getElementById(`work-tab-${next.id}`)?.focus();
                  }
                }}
              >
                {entry.title ?? t(entry.path.labelKey)}
                {entry.dirty ? ' •' : ''}
              </button>
              <button
                type="button"
                disabled={entry.busy}
                className="flex size-11 items-center justify-center rounded-md text-muted-foreground hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring"
                aria-label={t('workTabs.close', { name: entry.title ?? t(entry.path.labelKey) })}
                onClick={() => close(entry.id)}
              >
                <X className="size-4" aria-hidden="true" />
              </button>
            </div>
          ))}
          <InlineHelp articleId="settings.appearance" label={t('help.open')} />
        </div>
      )}
      <div className={companion ? 'work-area work-area-split' : 'work-area'}>
        {allowedEntries.map((entry) => (
          <Panel
            key={entry.id}
            entry={entry}
            active={entry.id === active?.id}
            companion={entry.id === companion?.id}
            update={update}
            close={close}
          />
        ))}
        {path === null && outlet}
        {path !== null && !isPathAvailable(location.pathname) && (
          <p role="alert">{t('permissions.deniedTitle')}</p>
        )}
      </div>
      <AlertDialog
        open={pending !== undefined}
        onOpenChange={(open) => {
          if (!open) setConfirmId(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogTitle>{t('workTabs.unsavedTitle')}</AlertDialogTitle>
          <AlertDialogDescription>
            {t('workTabs.unsavedDescription', {
              name: pending?.title ?? (pending ? t(pending.path.labelKey) : ''),
            })}
          </AlertDialogDescription>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('workTabs.keep')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (pending) remove(pending.id);
              }}
            >
              {t('workTabs.discard')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
